import { ReactNode, useCallback, useState } from 'react';
import { Features } from '../../config/features';
import { makeExternalDataInitialData, makeExternalDataSuccessData } from '../../helpers/external-data';
import { WithFeaturesProxy } from '../../helpers/with-features-proxy';
import SystemsHealthContext, { SystemsHealthCtxType } from './systems-health-context';
import systemsHealthService from './systems-health.service';

interface ExposedProps {
  children: ReactNode;
}

interface Props extends ExposedProps {
  hasAmwayProfile: boolean;
  hasExponea: boolean;
  hasOrderLookupAPI: boolean;
  hasProductInfoAPI: boolean;
  hasVolumeAPI: boolean;
  hasUplines: boolean;
  hasDocgen: boolean;
  hasPublisherAPI: boolean;
  hasNgcOrderDetailsApi: boolean;
  hasNgcReturnDetailsApi: boolean;
  hasNgcAccountDetailsApi: boolean;
  hasNgcAccountMappingsApi: boolean;
  hasNgcAccountsByProfileIdApi: boolean;
  hasNgcPaymentsApi: boolean;
  hasNgcProfileByPartyIdApi: boolean;
  hasNgcProfileApi: boolean;
  hasNgcUserApi: boolean;
  hasNgcAccountEmailsApi: boolean;
  hasNgcAccountPhonesApi: boolean;
  hasNgcAnonymousDeliveryTrackingApi: boolean;
}

const SystemsHealthProvider = ({
  children,
  hasAmwayProfile,
  hasExponea,
  hasOrderLookupAPI,
  hasProductInfoAPI,
  hasVolumeAPI,
  hasUplines,
  hasDocgen,
  hasPublisherAPI,
  hasNgcOrderDetailsApi,
  hasNgcReturnDetailsApi,
  hasNgcAccountDetailsApi,
  hasNgcAccountMappingsApi,
  hasNgcAccountsByProfileIdApi,
  hasNgcPaymentsApi,
  hasNgcProfileByPartyIdApi,
  hasNgcProfileApi,
  hasNgcUserApi,
  hasNgcAccountEmailsApi,
  hasNgcAccountPhonesApi,
  hasNgcAnonymousDeliveryTrackingApi,
}: Props) => {
  const [systemsHealth, setSystemsHealth] = useState<SystemsHealthCtxType['systemsHealth']>({
    self: makeExternalDataInitialData(),
    amwayProfile: makeExternalDataInitialData(),
    uplines: makeExternalDataInitialData(),
    docgen: makeExternalDataInitialData(),
    orderApi: makeExternalDataInitialData(),
    itemApi: makeExternalDataInitialData(),
    volumeApi: makeExternalDataInitialData(),
    exponea: makeExternalDataInitialData(),
    publisherApi: makeExternalDataInitialData(),
    ngcOrderDetailsApi: makeExternalDataInitialData(),
    ngcReturnDetailsApi: makeExternalDataInitialData(),
    ngcAccountDetailsApi: makeExternalDataInitialData(),
    ngcAccountMappingsApi: makeExternalDataInitialData(),
    ngcAccountsByProfileIdApi: makeExternalDataInitialData(),
    ngcPaymentsApi: makeExternalDataInitialData(),
    ngcProfileByPartyIdApi: makeExternalDataInitialData(),
    ngcProfileApi: makeExternalDataInitialData(),
    ngcUserApi: makeExternalDataInitialData(),
    ngcAccountEmailsApi: makeExternalDataInitialData(),
    ngcAccountPhonesApi: makeExternalDataInitialData(),
    ngcAnonymousDeliveryTrackingApi: makeExternalDataInitialData(),
  });

  const fetchSystemsHealth = useCallback(async () => {
    try {
      setSystemsHealth({
        self: makeExternalDataInitialData(),
        amwayProfile: makeExternalDataInitialData(),
        uplines: makeExternalDataInitialData(),
        docgen: makeExternalDataInitialData(),
        orderApi: makeExternalDataInitialData(),
        itemApi: makeExternalDataInitialData(),
        volumeApi: makeExternalDataInitialData(),
        exponea: makeExternalDataInitialData(),
        publisherApi: makeExternalDataInitialData(),
        ngcOrderDetailsApi: makeExternalDataInitialData(),
        ngcReturnDetailsApi: makeExternalDataInitialData(),
        ngcAccountDetailsApi: makeExternalDataInitialData(),
        ngcAccountMappingsApi: makeExternalDataInitialData(),
        ngcAccountsByProfileIdApi: makeExternalDataInitialData(),
        ngcPaymentsApi: makeExternalDataInitialData(),
        ngcProfileByPartyIdApi: makeExternalDataInitialData(),
        ngcProfileApi: makeExternalDataInitialData(),
        ngcUserApi: makeExternalDataInitialData(),
        ngcAccountEmailsApi: makeExternalDataInitialData(),
        ngcAccountPhonesApi: makeExternalDataInitialData(),
        ngcAnonymousDeliveryTrackingApi: makeExternalDataInitialData(),
      });

      systemsHealthService
        .getSelfStatus()
        .then(s => setSystemsHealth(prev => ({ ...prev, self: makeExternalDataSuccessData(s) })));
      hasAmwayProfile &&
        systemsHealthService
          .getAmwayProfileStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, amwayProfile: makeExternalDataSuccessData(s) })));
      hasExponea &&
        systemsHealthService
          .getExponeaStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, exponea: makeExternalDataSuccessData(s) })));
      hasOrderLookupAPI &&
        systemsHealthService
          .getOrderApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, orderApi: makeExternalDataSuccessData(s) })));
      hasProductInfoAPI &&
        systemsHealthService
          .getItemApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, itemApi: makeExternalDataSuccessData(s) })));
      hasVolumeAPI &&
        systemsHealthService
          .getVolumeApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, volumeApi: makeExternalDataSuccessData(s) })));
      hasDocgen &&
        systemsHealthService
          .getDocgenStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, docgen: makeExternalDataSuccessData(s) })));
      hasUplines &&
        systemsHealthService
          .getUplinesStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, uplines: makeExternalDataSuccessData(s) })));
      hasPublisherAPI &&
        systemsHealthService
          .getPublishAPIStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, publisherApi: makeExternalDataSuccessData(s) })));
      hasNgcOrderDetailsApi &&
        systemsHealthService
          .getNgcOrderDetailsApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, ngcOrderDetailsApi: makeExternalDataSuccessData(s) })));
      hasNgcReturnDetailsApi &&
        systemsHealthService
          .getNgcReturnDetailsApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, ngcReturnDetailsApi: makeExternalDataSuccessData(s) })));
      hasNgcAccountDetailsApi &&
        systemsHealthService
          .getNgcAccountDetailsApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, ngcAccountDetailsApi: makeExternalDataSuccessData(s) })));
      hasNgcAccountMappingsApi &&
        systemsHealthService
          .getNgcAccountMappingsApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, ngcAccountMappingsApi: makeExternalDataSuccessData(s) })));
      hasNgcAccountsByProfileIdApi &&
        systemsHealthService
          .getNgcAccountsByProfileIdApiStatus()
          .then(s =>
            setSystemsHealth(prev => ({ ...prev, ngcAccountsByProfileIdApi: makeExternalDataSuccessData(s) })),
          );
      hasNgcPaymentsApi &&
        systemsHealthService
          .getNgcPaymentsApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, ngcPaymentsApi: makeExternalDataSuccessData(s) })));
      hasNgcProfileByPartyIdApi &&
        systemsHealthService
          .getNgcProfileByPartyIdStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, ngcProfileByPartyIdApi: makeExternalDataSuccessData(s) })));
      hasNgcProfileApi &&
        systemsHealthService
          .getNgcProfileApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, ngcProfileApi: makeExternalDataSuccessData(s) })));
      hasNgcUserApi &&
        systemsHealthService
          .getNgcUserApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, ngcUserApi: makeExternalDataSuccessData(s) })));
      hasNgcAccountEmailsApi &&
        systemsHealthService
          .getNgcAccountEmailsApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, ngcAccountEmailsApi: makeExternalDataSuccessData(s) })));
      hasNgcAccountPhonesApi &&
        systemsHealthService
          .getNgcAccountPhonesApiStatus()
          .then(s => setSystemsHealth(prev => ({ ...prev, ngcAccountPhonesApi: makeExternalDataSuccessData(s) })));
      hasNgcAnonymousDeliveryTrackingApi &&
        systemsHealthService
          .getNgcAnonymousDeliveryTrackingApi()
          .then(s =>
            setSystemsHealth(prev => ({ ...prev, ngcAnonymousDeliveryTrackingApi: makeExternalDataSuccessData(s) })),
          );
    } catch (err) {
      console.log('unexpected error');
      console.error(err);
    }
  }, [
    hasAmwayProfile,
    hasDocgen,
    hasExponea,
    hasOrderLookupAPI,
    hasProductInfoAPI,
    hasPublisherAPI,
    hasUplines,
    hasVolumeAPI,
    hasNgcOrderDetailsApi,
    hasNgcReturnDetailsApi,
    hasNgcAccountDetailsApi,
    hasNgcAccountMappingsApi,
    hasNgcAccountsByProfileIdApi,
    hasNgcPaymentsApi,
    hasNgcProfileByPartyIdApi,
    hasNgcProfileApi,
    hasNgcUserApi,
    hasNgcAccountEmailsApi,
    hasNgcAccountPhonesApi,
    hasNgcAnonymousDeliveryTrackingApi,
  ]);

  return (
    <SystemsHealthContext.Provider value={{ systemsHealth, fetchSystemsHealth }}>
      {children}
    </SystemsHealthContext.Provider>
  );
};

export default WithFeaturesProxy<ExposedProps>(
  Features.MktFeature_Health_AmwayProfile,
  Features.MktFeature_Health_Exponea,
  Features.MktFeature_Health_OrderLookupAPI,
  Features.MktFeature_Health_ProductInfoAPI,
  Features.MktFeature_Health_VolumeAPI,
  Features.MktFeature_Health_Uplines,
  Features.MktFeature_Health_Docgen,
  Features.MktFeature_Health_PublisherAPI,
  Features.MktFeature_Health_NGCOrderDetailAPI,
  Features.MktFeature_Health_NGCReturnDetailAPI,
  Features.MktFeature_Health_NGCAccountDetailsAPI,
  Features.MktFeature_Health_NGCAccountMappingsAPI,
  Features.MktFeature_Health_NGCAccountsByProfileIdAPI,
  Features.MktFeature_Health_NGCPaymentsAPI,
  Features.MktFeature_Health_NGCProfileByPartyIdAPI,
  Features.MktFeature_Health_NGCProfileAPI,
  Features.MktFeature_Health_NGCUserAPI,
  Features.MktFeature_Health_NGCAccountEmailsAPI,
  Features.MktFeature_Health_NGCAccountPhonesAPI,
  Features.MktFeature_Health_NGCAnonymousDeliveryTrackingAPI,
)((props, ...hasFeatures) => {
  const [
    hasAmwayProfile,
    hasExponea,
    hasOrderLookupAPI,
    hasProductInfoAPI,
    hasVolumeAPI,
    hasUplines,
    hasDocgen,
    hasPublisherAPI,
    hasNgcOrderDetailsApi,
    hasNgcReturnDetailsApi,
    hasNgcAccountDetailsApi,
    hasNgcAccountMappingsApi,
    hasNgcAccountsByProfileIdApi,
    hasNgcPaymentsApi,
    hasNgcProfileByPartyIdApi,
    hasNgcProfileApi,
    hasNgcUserApi,
    hasNgcAccountEmailsApi,
    hasNgcAccountPhonesApi,
    hasNgcAnonymousDeliveryTrackingApi,
  ] = hasFeatures;
  return (
    <SystemsHealthProvider
      {...props}
      hasAmwayProfile={hasAmwayProfile}
      hasExponea={hasExponea}
      hasOrderLookupAPI={hasOrderLookupAPI}
      hasProductInfoAPI={hasProductInfoAPI}
      hasVolumeAPI={hasVolumeAPI}
      hasUplines={hasUplines}
      hasDocgen={hasDocgen}
      hasPublisherAPI={hasPublisherAPI}
      hasNgcOrderDetailsApi={hasNgcOrderDetailsApi}
      hasNgcReturnDetailsApi={hasNgcReturnDetailsApi}
      hasNgcAccountDetailsApi={hasNgcAccountDetailsApi}
      hasNgcAccountMappingsApi={hasNgcAccountMappingsApi}
      hasNgcAccountsByProfileIdApi={hasNgcAccountsByProfileIdApi}
      hasNgcPaymentsApi={hasNgcPaymentsApi}
      hasNgcProfileByPartyIdApi={hasNgcProfileByPartyIdApi}
      hasNgcProfileApi={hasNgcProfileApi}
      hasNgcUserApi={hasNgcUserApi}
      hasNgcAccountEmailsApi={hasNgcAccountEmailsApi}
      hasNgcAccountPhonesApi={hasNgcAccountPhonesApi}
      hasNgcAnonymousDeliveryTrackingApi={hasNgcAnonymousDeliveryTrackingApi}
    />
  );
});
